/* === ChatWindow.js === */
import React, { useState, useEffect, useRef } from 'react';
import { 
  validateCitationLinks, 
  formatGenericMessage, 
  isValidJSON,
  extractJsonFromMarkdownFence
} from './ChatUtils';
import { InputContainer } from './InputContainer';
import { MessageList } from './MessageList';
import { MessageHeader } from './MessageHeader';
import { initializeSession } from './ChatUtils';
import { api } from '../api/robyBackend';
import { startLektoratJob, pollLektoratUntilDone, fetchLektoratChanges } from './LektoratUtils';
import './ChatWindow.css';
import RecordRTC from 'recordrtc';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// Baut aus `corrected` HTML, bei dem nur die Wörter fett sind, die sich gegenüber
// `original` tatsächlich geändert haben (Wort-Diff via LCS), statt den ganzen Vorschlag
// fett zu markieren.
const boldOnlyChangedWords = (original, corrected) => {
  const tokenize = (s) => (s || '').split(/(\s+)/);
  const a = tokenize(original);
  const b = tokenize(corrected);
  const m = a.length;
  const n = b.length;

  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const commonB = new Array(n).fill(false);
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      commonB[j] = true;
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }

  return b
    .map((token, idx) => {
      const escaped = escapeHtml(token);
      if (commonB[idx] || token.trim() === '') return escaped;
      return `<strong>${escaped}</strong>`;
    })
    .join('');
};

const ChatWindow = ({ session, handleNewSessionApp, app, setApp, toggleSidebar, isVisible, refreshSessions, refreshCurrentSession }) => {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [divCount] = useState(30);
  const mediaRecorderRef = useRef(null);
  const messagesEndRef = useRef(null);
  const animationRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [, setIsRunning] = useState(false);
  const [isJsonResponse] = useState(false);
  const [lastJsonResponse] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const previousSessionIdRef = useRef(null);
  const [processingDocument, setProcessingDocument] = useState(false);
  const [processingMessage, setProcessingMessage] = useState(null);
  const [processingSuccess, setProcessingSuccess] = useState(false);
  const processingCheckInterval = useRef(null);
  const successTimeoutRef = useRef(null);
  const isCheckingStatus = useRef(false);
  const processingStartTime = useRef(null); // Zeitpunkt, wann Processing begonnen hat
  const minProcessingDisplayTime = 1000; // Mindestens 1 Sekunde Processing-Message anzeigen
  const [checkAgents, setCheckAgents] = useState([]);
  const [checkingMessageId, setCheckingMessageId] = useState(null);
  const [buttonPrompts, setButtonPrompts] = useState([]);
  const [fullscreenPrompt, setFullscreenPrompt] = useState(null);
  const [pendingButtonPrompt, setPendingButtonPrompt] = useState(null);
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const pendingFileInputRef = useRef(null);
  const [lektoratExport, setLektoratExport] = useState(false);

  useEffect(() => {
    if (!session) return;

    // Verwende session.app falls app noch nicht gesetzt ist
    const appId = app || session.app;
    if (!appId) {
      console.warn('No app_id available for session initialization');
      return;
    }

    // Prüfe ob sich die Session ID wirklich geändert hat
    const isSessionChange = previousSessionIdRef.current !== null &&
                           previousSessionIdRef.current !== session._id;

    // Bei Session-Wechsel sofort isRunning zurücksetzen
    if (isSessionChange) {
      setIsRunning(false);
    }

    // Capture current session ID to prevent race conditions
    const currentSessionId = session._id;
    let cancelled = false;

    const debounceTimeout = setTimeout(() => {
      const runInitializeSession = async () => {
        if (cancelled) return;

        // Wenn session.history fehlt, lade die vollständige Session
        let fullSession = session;
        if (!session.history) {
          console.log('🔄 Loading full session with history for:', currentSessionId);
          setIsLoading(true);
          try {
            const response = await api.get(`/database/sessions/${currentSessionId}`);
            fullSession = response.data;
          } catch (error) {
            if (!cancelled) {
              console.error('❌ Error loading full session:', error);
            }
            setIsLoading(false);
            return;
          }
          setIsLoading(false);
        }

        if (cancelled) return;
        setIsRunning(true);
        try {
          // Übergebe ob es ein Session-Wechsel ist (User kommt zu dieser Session zurück)
          await initializeSession(fullSession, setMessages, appId, isSessionChange);

          // Nur updaten wenn immer noch die gleiche Session
          if (!cancelled && session._id === currentSessionId) {
            previousSessionIdRef.current = session._id;
          } else {
            console.log('⚠️ Session changed during init, skipping update');
          }
        } catch (error) {
          if (!cancelled) {
            console.error('❌ Error initializing session:', error);
          }
        } finally {
          if (!cancelled) {
            setIsRunning(false);
          }
        }
      };
      runInitializeSession();
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimeout);
    };
  }, [session?._id, app]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (session?.app) {
      setApp(session.app);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Check-Agents und Button-Prompts aus App-Config laden
  useEffect(() => {
    if (!app) return;
    let cancelled = false;
    const loadAppConfig = async () => {
      try {
        const response = await api.get(`/database/apps/${app}`);
        if (cancelled) return;
        const appConfig = response.data?.app_config || {};
        const tools = appConfig.tools || [];
        const agentCallTools = tools.filter(t => t.type === 'message_check');
        setCheckAgents(agentCallTools);
        setButtonPrompts(appConfig.button_prompts || []);
        setLektoratExport(appConfig.lektorat_export || false);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading app config:', error);
          setCheckAgents([]);
          setButtonPrompts([]);
        }
      }
    };
    loadAppConfig();
    return () => { cancelled = true; };
  }, [app]);

  // Separate useEffect um processing_status Updates zu erkennen
  useEffect(() => {
    if (!session) return;

    // Update processing states wenn session sich ändert (auch wenn gleiche ID)
    const sessionProcessing = session.processing_document || false;
    const sessionMessage = session.processing_message || null;
    const sessionSuccess = session.processing_success || false;

    console.log('🔄 Session processing state changed:', {
      sessionProcessing,
      sessionMessage,
      sessionSuccess,
      sessionId: session._id
    });

    // Setze States nur wenn sie sich wirklich ändern
    setProcessingDocument(prev => {
      if (prev !== sessionProcessing) {
        console.log('  ↳ processingDocument:', prev, '→', sessionProcessing);

        // Wenn Processing startet, merke dir den Zeitpunkt
        if (sessionProcessing && !prev) {
          processingStartTime.current = Date.now();
        }

        // Wenn Processing stoppt, prüfe ob Mindest-Anzeigezeit erreicht wurde
        if (!sessionProcessing && prev && processingStartTime.current) {
          const elapsed = Date.now() - processingStartTime.current;
          if (elapsed < minProcessingDisplayTime) {
            // Warte noch bis Mindest-Anzeigezeit erreicht ist
            const remainingTime = minProcessingDisplayTime - elapsed;
            console.log(`  ⏱️ Warte noch ${remainingTime}ms bis Processing-Message ausgeblendet wird`);
            setTimeout(() => {
              setProcessingDocument(false);
            }, remainingTime);
            return prev; // Behalte true
          }
        }

        return sessionProcessing;
      }
      return prev;
    });

    setProcessingMessage(prev => {
      if (prev !== sessionMessage) {
        console.log('  ↳ processingMessage:', prev, '→', sessionMessage);
        return sessionMessage;
      }
      return prev;
    });

    setProcessingSuccess(prev => {
      if (prev !== sessionSuccess) {
        console.log('  ↳ processingSuccess:', prev, '→', sessionSuccess);
        return sessionSuccess;
      }
      return prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.processing_document, session?.processing_message, session?.processing_success]);

  // Dokument-Verarbeitung überwachen
  useEffect(() => {
    if (!session?._id) return;

    console.log('📡 Polling useEffect triggered for session:', session._id);
    console.log('   processing_document:', session.processing_document);
    console.log('   processing_message:', session.processing_message);
    console.log('   processing_success:', session.processing_success);

    // States werden vom separaten useEffect (Zeile 136-175) gesetzt
    // NICHT hier setzen, um Konflikte zu vermeiden!

    // Success-Message kommt jetzt direkt aus der DB-History, keine temporäre Message mehr nötig

    // Cleanup-Flag für diesen spezifischen useEffect
    let isMounted = true;
    const currentSessionId = session._id;
    const abortControllerRef = { current: null };

    const checkProcessingStatus = async () => {
      // Verhindere gleichzeitige Requests oder wenn unmounted
      if (isCheckingStatus.current || !isMounted) {
        return;
      }

      isCheckingStatus.current = true;

      try {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 Sekunden Timeout

        const response = await api.get(`/database/sessions/${currentSessionId}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        abortControllerRef.current = null;

        // Prüfe erneut ob Component noch mounted ist
        if (!isMounted) {
          return;
        }

        const sessionData = response.data;

        // WICHTIG: "backendProcessing" statt "isProcessing" um Shadowing des Component-States zu vermeiden
        const backendProcessing = sessionData.processing_document || false;
        const message = sessionData.processing_message || null;
        const isSuccess = sessionData.processing_success || false;

        console.log('📊 Processing status:', { backendProcessing, message, isSuccess, historyLength: sessionData.history?.length });

        // Reset Error-Counter bei erfolgreichem Request
        if (window.processingErrorCount) {
          window.processingErrorCount = 0;
        }

        // Prüfe ob gerade ein Chat-Request läuft (Typing-Indicator aktiv ODER isLoading)
        const hasActiveTyping = messages.some(m => m.type === 'typing');
        const isChatRequestActive = hasActiveTyping || isLoadingRef.current;

        // Prüfe ob die DB neue Nachrichten hat (Backend hat Bot-Antwort gespeichert)
        const currentNonTypingCount = messages.filter(m => m.type !== 'typing').length;
        const dbHasNewMessages = (sessionData?.history?.length || 0) > currentNonTypingCount;

        // History aktualisieren wenn:
        // 1. Kein Chat-Request aktiv ist, ODER
        // 2. Die DB mehr Nachrichten hat als die UI (Backend hat Bot-Antwort gespeichert)
        if (sessionData?.history && (!isChatRequestActive || dbHasNewMessages)) {
          if (dbHasNewMessages) {
            console.log(`📨 DB hat neue Nachrichten (DB: ${sessionData.history.length}, UI: ${currentNonTypingCount}) - lade aus DB`);
          }
          const historyWithFormats = await Promise.all(sessionData.history.map(async (msg) => {
            const isBot = (msg.user && msg.user.toLowerCase() === 'bot') ||
                          (msg.role && msg.role.toLowerCase() === 'bot') ||
                          (msg.role && msg.role.toLowerCase() === 'assistant');
            const rawText = msg.text || msg.content || '';

            // Success-Messages, Error-Messages und JSON-Responses nicht formatieren
            const isSuccessMessage = msg.type === 'success';
            const isErrorMessage = msg.type === 'error';
            const isJsonMsg = msg.is_json_response === true;
            // JSON-Responses NICHT mit formatGenericMessage formatieren (würde JSON als Markdown interpretieren)
            const formattedText = (isBot && !isSuccessMessage && !isErrorMessage && !isJsonMsg) ? formatGenericMessage(rawText) : rawText;

            // Generiere SAS URL für Audio-Nachrichten
            let audioUrl = msg.audioUrl || null;
            if (msg.type === 'audio' && msg.blobPath && !audioUrl) {
              try {
                const sasResponse = await api.post('/chat/get-sas-url', { blobPath: msg.blobPath });
                audioUrl = sasResponse.data.url;
              } catch (error) {
                console.error('❌ Fehler beim Generieren der SAS URL im Polling:', error);
              }
            }

            return {
              ...msg,
              user: msg.user || (msg.role === 'assistant' || msg.role === 'bot' ? 'Bot' : 'User'),
              text: formattedText,
              timestamp: msg.timestamp || msg.updatetime || new Date().toISOString(),
              id: msg.id || Date.now() + Math.random(),
              type: msg.type || 'msg',  // Explizit type beibehalten aus DB
              totalTokens: msg.total_tokens,
              citations: msg.citations,
              isJsonResponse: msg.is_json_response,
              jsonData: msg.json_data,
              isCitationResponse: msg.citations && Array.isArray(msg.citations) && msg.citations.length > 0,
              checkedBy: msg.checked_by,
              blobPath: msg.blobPath,
              audioUrl: audioUrl
            };
          }));

          // History wird immer aktualisiert - Success-Message kommt bereits aus der DB
          console.log('📊 Polling: Setting messages from DB, count:', historyWithFormats.length);

          // Debug: Log alle Success-Messages
          const successMessages = historyWithFormats.filter(m => m.type === 'success');
          if (successMessages.length > 0) {
            console.log('✅ Polling found', successMessages.length, 'success message(s):', successMessages.map(m => ({ text: m.text?.substring(0, 50), type: m.type })));
          } else {
            console.log('⚠️ Polling: No success messages found in history');
          }

          setMessages(historyWithFormats);

          // Wenn neue Bot-Nachricht aus DB geladen wurde, Loading-State zurücksetzen
          if (dbHasNewMessages) {
            console.log('✅ Bot-Antwort aus DB geladen - setze Loading zurück');
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsProcessing(false);
          }
        }

        // Wenn erfolgreich, setze sofort alle States BEVOR der 3-Sekunden Timer startet
        // So ist der Input sofort wieder freigegeben
        if (isSuccess) {
          console.log('🎉 Success! History updated with', sessionData.history?.length, 'messages');

          // Sofort Processing-States zurücksetzen, ABER processingSuccess auf true lassen
          setProcessingDocument(false); // Input sofort freigegeben
          setProcessingMessage(null);    // Processing-Animation sofort weg
          setProcessingSuccess(true);    // Success-Flag bleibt TRUE für 3 Sekunden

          // Stoppe Polling sofort bei Erfolg
          if (processingCheckInterval.current) {
            clearInterval(processingCheckInterval.current);
            processingCheckInterval.current = null;
          }

          // Cleanup vorheriger Timeout
          if (successTimeoutRef.current) {
            clearTimeout(successTimeoutRef.current);
          }

          // Session-History wurde bereits oben aktualisiert (Zeile 256-294)
          // Success-Message ist bereits in den messages enthalten
          console.log('✅ Success message already loaded from polling');

          // Nach 3 Sekunden Backend-Status zurücksetzen
          // Success-Message bleibt dauerhaft in der History (kommt aus DB)
          successTimeoutRef.current = setTimeout(async () => {
            if (!isMounted) return;

            // Success-Flag zurücksetzen
            setProcessingSuccess(false);

            // Backend-Status zurücksetzen
            try {
              await api.patch(`/database/sessions/${currentSessionId}`, {
                processing_document: false,
                processing_message: null,
                processing_success: false
              });
            } catch (err) {
              console.error('Error resetting processing status:', err);
            }
          }, 3000);
        } else if (!backendProcessing && !isSuccess) {
          // Backend Processing ist beendet
          // Prüfe ob eine neue Bot-Nachricht in der DB-History vorhanden ist
          const currentMsgCount = messages.filter(m => m.type !== 'typing').length;
          const dbMsgCount = sessionData.history?.length || 0;
          const hasNewBotMessage = dbMsgCount > currentMsgCount;

          if (hasNewBotMessage) {
            // Neue Nachricht in DB gefunden! Messages werden oben bereits aktualisiert
            console.log(`✅ Neue Bot-Nachricht in DB gefunden (DB: ${dbMsgCount}, UI: ${currentMsgCount}) - zeige an`);
          }

          // Polling stoppen
          console.log('✅ Processing finished, stopping poll');
          setProcessingDocument(false);
          setProcessingMessage(null);
          setProcessingSuccess(false);

          if (processingCheckInterval.current) {
            clearInterval(processingCheckInterval.current);
            processingCheckInterval.current = null;
          }
        } else {
          // Normal processing läuft noch - Backend-Progress anzeigen
          setProcessingDocument(backendProcessing);
          setProcessingMessage(message);
          setProcessingSuccess(false);
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          // Abgebrochene Requests sind normal beim Session-Wechsel
          console.log('Processing status check was cancelled (session change)');
          return;
        }

        console.error('Error checking processing status:', error);

        // Bei wiederholten Fehlern: Stoppe Polling nach 3 Versuchen
        if (!window.processingErrorCount) window.processingErrorCount = 0;
        window.processingErrorCount++;

        if (window.processingErrorCount >= 3) {
          console.warn('Too many errors checking processing status, stopping poll');
          if (processingCheckInterval.current) {
            clearInterval(processingCheckInterval.current);
            processingCheckInterval.current = null;
          }
          // Reset Processing-Status bei anhaltenden Fehlern
          setProcessingDocument(false);
          setProcessingMessage('Fehler beim Überprüfen des Status. Bitte Seite neu laden.');
          setProcessingSuccess(false);
          window.processingErrorCount = 0;
        }
      } finally {
        isCheckingStatus.current = false;
      }
    };

    // Prüfe ob ein Typing-Indicator aktiv ist (= Chat läuft gerade)
    const hasTypingIndicator = messages.some(m => m.type === 'typing');

    // Polling für Fortschritts-Updates:
    // - Bei Dokument-Upload (processingDocument)
    // - Bei Chunk-Verarbeitung während Chat (isProcessing + session?.processing_document)
    // - Bei Success-Anzeige (processingSuccess)
    // - Bei laufendem Chat-Request (isProcessing) → damit Backend-Progress (z.B. "Batch 2/5") abgeholt wird
    const shouldPoll = (
      processingDocument ||
      processingSuccess ||
      session?.processing_document ||
      session?.processing_success ||
      (isProcessing && session?.processing_document) ||
      isProcessing  // Auch während Chat-Request pollen für Chunk-Progress
    );

    // Starten wenn processing_document true ODER processing_success true
    // NICHT mehr bei typing indicator - das verursacht Race Conditions mit dem Chat-Response
    if (shouldPoll) {
      // Initial check nach kurzer Verzögerung
      const initialTimeout = setTimeout(() => {
        if (isMounted) {
          checkProcessingStatus();
        }
      }, 500);  // 🔴 Schnellerer initial check (500ms statt 1000ms)

      // Poll alle 2 Sekunden wenn typing indicator aktiv (für Echtzeit-Fortschritt)
      // Sonst alle 5 Sekunden
      const pollInterval = hasTypingIndicator ? 2000 : 5000;
      processingCheckInterval.current = setInterval(() => {
        if (isMounted) {
          checkProcessingStatus();
        }
      }, pollInterval);

      // Cleanup für initial timeout
      return () => {
        clearTimeout(initialTimeout);
        isMounted = false;
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        if (processingCheckInterval.current) {
          clearInterval(processingCheckInterval.current);
          processingCheckInterval.current = null;
        }
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
          successTimeoutRef.current = null;
        }
        isCheckingStatus.current = false;
      };
    }

    // Standard cleanup wenn kein Polling
    return () => {
      isMounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (processingCheckInterval.current) {
        clearInterval(processingCheckInterval.current);
        processingCheckInterval.current = null;
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
      isCheckingStatus.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?._id, session?.processing_document, session?.processing_success, isProcessing]);

  useEffect(() => {
    const scrollToBottom = () => {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    };
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (textOverride, skipProcessingCheck = false, webSearchActive = false, legalSearchActive = false) => {
    if (!session || !session._id) {
      console.error('No valid session provided when trying to send a message');
      return;
    }
    const messageText = textOverride !== undefined ? textOverride : message;
    // Blockiere nur wenn Processing läuft oder Dokument verarbeitet wird (aber nicht bei Success)
    if (messageText.trim() === '' || isProcessing || (!skipProcessingCheck && processingDocument && !processingSuccess)) return;

    // Speichere die Nachricht und lösche das Input sofort (nur bei manuellem Input)
    if (textOverride === undefined) setMessage('');

    setIsProcessing(true);
    setIsLoading(true);
    isLoadingRef.current = true;

    const userMessage = {
      user: 'User',
      text: messageText,
      timestamp: new Date().toISOString(),
      id: Date.now(),
      type: 'msg'
    };
    setMessages(prev => [...prev, userMessage]);

    const typingIndicator = { 
      user: 'Bot', 
      text: '', 
      timestamp: new Date().toISOString(), 
      id: 'typing-' + Date.now(), 
      type: 'typing' 
    };
    setMessages(prev => [...prev, typingIndicator]);

    try {
      // KORRIGIERT: timestamp INNERHALB des message-Objekts
      await api.post(`/database/sessions/${session._id}/message`, {
        message: {
          user: 'User',
          text: messageText,
          type: 'msg',
          timestamp: new Date().toISOString()
        },
        display: messageText.slice(0, 20) // Die ersten 20 Zeichen der Frage als Titel
      });

      // Bot-Antwort abrufen (längeres Timeout für Document Analyzer mit Chunk-Analyse)
      const botResponse = await api.post(`/chat/${app}/${session._id}/message`, { message: messageText, web_search_active: webSearchActive, legal_search_active: legalSearchActive }, { timeout: 600000 });
      console.log('🔍 DEBUG botResponse.data:', botResponse.data);
      console.log('🔍 DEBUG botResponse.data.output length:', botResponse.data?.output?.length);
      const outputData = botResponse.data.output;
      const parsedFromFence = extractJsonFromMarkdownFence(outputData);
      const displayOutput = (parsedFromFence && typeof parsedFromFence === 'object' && typeof parsedFromFence.answer === 'string')
        ? parsedFromFence.answer
        : outputData;

      const citationsData = botResponse?.data?.citations ?? parsedFromFence?.citations;
      const totalTokens = botResponse?.data?.total_tokens;

      const completeCitations = citationsData ? citationsData.map(citation => ({
        page: citation.page || null,
        document: citation.document || null,
        url: citation.url || null,
        file: citation.file || null,
        extract: citation.extract || null,
        title: citation.title || null,
        type: citation.type || null
      })) : null;

      const validatedCitations = completeCitations ? 
        await validateCitationLinks(completeCitations) : null;

      const citationFlag = validatedCitations && Array.isArray(validatedCitations) && validatedCitations.length > 0;
      const isJsonResponse = isValidJSON(outputData);

      // JSON-Daten extrahieren (auch aus Markdown-Blöcken)
      let jsonData = null;
      if (isJsonResponse) {
        try {
          jsonData = JSON.parse(outputData);
        } catch {
          // Falls direktes Parsen fehlschlägt, versuche aus Markdown-Block zu extrahieren
          jsonData = extractJsonFromMarkdownFence(outputData);
        }
      }

      // WICHTIG: Wenn es eine JSON-Response ist, NICHT formatGenericMessage verwenden!
      // formatGenericMessage würde den JSON-String als Markdown interpretieren
      // Stattdessen: outputData unverändert lassen, wird später in MessageList mit formatResponseMessageGenerically angezeigt
      const formattedBotText = isJsonResponse ? outputData : formatGenericMessage(displayOutput);

      const newBotMsg = {
        user: 'Bot',
        text: formattedBotText,
        timestamp: new Date().toISOString(),
        id: Date.now(),
        type: 'msg',
        totalTokens: totalTokens,
        citations: validatedCitations,
        isJsonResponse: isJsonResponse,
        jsonData: jsonData,
        isCitationResponse: citationFlag
      };

      // 🔴 WICHTIG: Bot-Nachricht SOFORT anzeigen - vor allen weiteren API-Calls
      // Damit ist die Nachricht sichtbar, auch wenn spätere Calls fehlschlagen
      setMessages(prev => {
        const withoutTyping = prev.filter(m => m.type !== 'typing');
        return [...withoutTyping, newBotMsg];
      });

      // Ab hier: Hintergrund-Operationen (DB-Speicherung, Session-Refresh)
      // Diese dürfen die Nachricht NICHT mehr beeinflussen
      try {
        // Berechne display-Titel: Bei Report-JSON verwende den Berichttitel, sonst den ersten Text
        let displayTitle;
        if (isJsonResponse && jsonData && jsonData['Report Details'] && Array.isArray(jsonData['Report Details']) && jsonData['Report Details'][0]) {
          const reportDetails = jsonData['Report Details'][0];
          const reportTitle = reportDetails['Titel'] || reportDetails['Berichttitel'] || reportDetails['Report Title'] || reportDetails['Title'] || reportDetails['Detaillierter Titel'] || reportDetails['Kurztitel'];
          if (reportTitle) {
            displayTitle = reportTitle.length > 20 ? reportTitle.substring(0, 20).trim() + '...' : reportTitle;
          } else {
            displayTitle = 'Bericht';
          }
        } else if (isJsonResponse) {
          displayTitle = 'Bericht';
        } else {
          displayTitle = formattedBotText.replace(/(<([^>]+)>)/gi, '').slice(0, 25) + '...';
        }

        // Bot-Nachricht wird bereits vom Backend in die DB gespeichert
        // Nur den Display-Titel aktualisieren
        await api.patch(`/database/sessions/${session._id}`, {
          display: displayTitle
        });

        // Session-Liste aktualisieren (Hintergrund)
        if (refreshSessions) {
          await refreshSessions(session._id);
        }
      } catch (bgError) {
        // Hintergrund-Fehler nur loggen, nicht dem User anzeigen
        // Die Nachricht wurde bereits erfolgreich angezeigt
        console.warn('Hintergrund-Speicherung fehlgeschlagen (Nachricht wurde trotzdem angezeigt):', bgError);
      }

    } catch (error) {
      // Ignoriere abgebrochene Requests (normal beim Session-Wechsel)
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('Request was cancelled (session change)');
        return;
      }

      console.error('Error sending message:', error);

      // 🔴 Nur Fehlermeldung anzeigen wenn die Bot-Antwort NICHT erfolgreich war
      // (d.h. wenn der Chat-API-Call selbst fehlgeschlagen ist)
      const errorMsg = {
        user: 'Bot',
        text: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        timestamp: new Date().toISOString(),
        id: Date.now(),
        isError: true
      };

      setMessages((prev) => {
        const withoutTyping = prev.filter(m => m.type !== 'typing');
        return [...withoutTyping, errorMsg];
      });
    } finally {
      // Garantiere, dass Processing-States IMMER zurückgesetzt werden
      isLoadingRef.current = false;
      setIsLoading(false);
      setIsProcessing(false);
      setProcessingDocument(false);
      setProcessingMessage(null);
    }
  };

  const handleCheckMessage = async (messageText, agentId) => {
    if (!session || !session._id) return;

    const checkTypingId = 'check-typing-' + Date.now();
    const checkTyping = {
      user: 'Check',
      text: '',
      timestamp: new Date().toISOString(),
      id: checkTypingId,
      type: 'typing'
    };
    setMessages(prev => [...prev, checkTyping]);
    setCheckingMessageId(agentId);

    try {
      const response = await api.post(`/chat/check-message/${app}/${session._id}`, {
        message_text: messageText,
        agent_id: agentId
      }, { timeout: 600000 });

      const checkMsg = {
        user: 'Check',
        text: formatGenericMessage(response.data.output),
        timestamp: new Date().toISOString(),
        id: Date.now(),
        type: 'check',
        checkedBy: response.data.checked_by,
        totalTokens: response.data.total_tokens,
        status: response.data.status || 'ok'
      };

      setMessages(prev => {
        const withoutTyping = prev.filter(m => m.id !== checkTypingId);
        return [...withoutTyping, checkMsg];
      });
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Error checking message:', error);
      const errorMsg = {
        user: 'Check',
        text: 'Fehler bei der Nachrichtenprüfung. Bitte versuchen Sie es erneut.',
        timestamp: new Date().toISOString(),
        id: Date.now(),
        type: 'check'
      };
      setMessages(prev => {
        const withoutTyping = prev.filter(m => m.id !== checkTypingId);
        return [...withoutTyping, errorMsg];
      });
    } finally {
      setCheckingMessageId(null);
    }
  };

  const handleButtonPromptClick = (bp) => {
    if (isProcessing || (processingDocument && !processingSuccess)) return;
    if (bp.required_documents > 0) {
      setPendingButtonPrompt(bp);
      setPendingUploadFiles([]);
    } else {
      handleSendMessage(bp.prompt);
    }
  };

  const handlePendingFileSelect = (e) => {
    const maxFiles = pendingButtonPrompt?.required_documents || 1;
    const files = Array.from(e.target.files).slice(0, maxFiles);
    setPendingUploadFiles(files);
  };

  // Baut aus den Lektorat-Änderungsvorschlägen eine Tabelle (Fundstelle/Original/Art der
  // Änderung/Vorschlag/Begründung) und hängt sie als Bot-Nachricht an den Chatverlauf an,
  // statt eine normale KI-Chat-Antwort zu senden (siehe lektoratExport-Zweig in handleUploadAndSend).
  const runLektoratTableFlow = async () => {
    setProcessingMessage('Lektorat wird verarbeitet…');
    try {
      await startLektoratJob(app, session._id);
      await pollLektoratUntilDone(app, session._id, {
        onProgress: (data) => setProcessingMessage(data.message || 'Lektorat wird verarbeitet…'),
      });

      const paragraphs = await fetchLektoratChanges(app, session._id);
      const rows = [];
      paragraphs.forEach((para) => {
        para.patches.forEach((patch) => {
          rows.push(
            `<tr><td>Absatz ${para.index + 1}</td><td>${escapeHtml(patch.original)}</td>` +
            `<td>${escapeHtml(patch.change_type)}</td><td>${boldOnlyChangedWords(patch.original, patch.corrected)}</td>` +
            `<td>${escapeHtml(patch.reason)}</td></tr>`
          );
        });
      });

      const tableHtml = rows.length > 0
        ? '<table><thead><tr><th>Fundstelle</th><th>Originaltext</th><th>Art der Änderung</th>' +
          `<th>Verbesserungsvorschlag</th><th>Begründung</th></tr></thead><tbody>${rows.join('')}</tbody></table>`
        : '<p>Es wurden keine Änderungsvorschläge gefunden.</p>';

      setMessages((prev) => [...prev, {
        id: `lektorat-table-${Date.now()}`,
        user: 'bot',
        text: tableHtml,
        timestamp: new Date().toISOString(),
        type: 'msg',
      }]);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
      setMessages((prev) => [...prev, {
        id: `lektorat-error-${Date.now()}`,
        user: 'bot',
        text: `❌ Fehler beim Lektorat: ${detail}`,
        timestamp: new Date().toISOString(),
        type: 'error',
      }]);
    } finally {
      setProcessingDocument(false);
      setProcessingMessage(null);
    }
  };

  const handleUploadAndSend = async () => {
    if (!pendingButtonPrompt || pendingUploadFiles.length === 0) return;
    const promptText = pendingButtonPrompt.prompt;
    const files = [...pendingUploadFiles];
    const skipChatAndRunLektorat = lektoratExport;
    setPendingButtonPrompt(null);
    setPendingUploadFiles([]);

    setProcessingDocument(true);
    setProcessingMessage(`Lade ${files.length} Dokument(e) hoch...`);

    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      await api.post(
        `/chat/ai_foundry_agent/upload/${app}/${session._id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 }
      );

      if (skipChatAndRunLektorat) {
        await runLektoratTableFlow();
        return;
      }

      // Session direkt laden um Success-Message in der richtigen Reihenfolge zu zeigen
      const sessionData = await api.get(`/database/sessions/${session._id}`);
      const history = sessionData.data?.history || [];
      const mappedHistory = history.map((msg, idx) => ({
        ...msg,
        user: msg.user || 'User',
        text: (msg.user?.toLowerCase() === 'bot' && msg.type !== 'success' && msg.type !== 'error' && !msg.is_json_response)
          ? formatGenericMessage(msg.text || msg.content || '')
          : (msg.text || msg.content || ''),
        timestamp: msg.timestamp || new Date().toISOString(),
        id: msg.id || (Date.now() + idx),
        type: msg.type || 'msg',
        totalTokens: msg.total_tokens,
        citations: msg.citations,
        isJsonResponse: msg.is_json_response,
        jsonData: msg.json_data,
        isCitationResponse: !!(msg.citations?.length > 0),
        checkedBy: msg.checked_by,
      }));

      // Messages mit Success-Message setzen + Processing-States zurücksetzen
      setMessages(mappedHistory);
      setProcessingDocument(false);
      setProcessingMessage(null);
      setProcessingSuccess(false);

      // Prompt senden (skipProcessingCheck=true da States oben explizit zurückgesetzt)
      await handleSendMessage(promptText, true);
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Fehler beim Dokument-Upload:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
      alert('Fehler beim Hochladen: ' + errorDetail);
      setProcessingDocument(false);
      setProcessingMessage(null);
    }
  };

  const animateWave = () => {
    if (animationRef.current && analyserRef.current) {
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      const bars = animationRef.current.querySelectorAll('div');
      bars.forEach((bar, index) => {
        const barHeight = Math.max(5, (dataArrayRef.current[index] / 255) * 65);
        bar.style.height = `${barHeight}px`;
      });
      animationFrameIdRef.current = requestAnimationFrame(animateWave);
    }
  };

  const startRecording = () => {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        mediaRecorderRef.current = new RecordRTC(stream, { type: 'audio', mimeType: 'audio/wav', recorderType: RecordRTC.StereoAudioRecorder, desiredSampRate: 16000 });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(stream);
        analyserRef.current = audioContext.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.85;
        source.connect(analyserRef.current);
        dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
        mediaRecorderRef.current.startRecording();
        setRecording(true); setPaused(false); setShowPlaceholder(false);
        setTimeout(() => { animateWave(); }, 100);
      })
      .catch((error) => console.error('Error accessing media devices.', error));
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      // Falls pausiert, erst fortsetzen bevor gestoppt wird
      if (paused) {
        mediaRecorderRef.current.resumeRecording();
      }

      mediaRecorderRef.current.stopRecording(() => {
        const audioBlob = mediaRecorderRef.current.getBlob();
        handleRecordingUpload(audioBlob);
        setRecording(false); setPaused(false);
        if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
      });
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && recording && !paused) {
      mediaRecorderRef.current.pauseRecording();
      setPaused(true);
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && recording && paused) {
      mediaRecorderRef.current.resumeRecording();
      setPaused(false);
      if (analyserRef.current && dataArrayRef.current) animateWave();
    }
  };

  const handleFileUpload = async (file) => {
    const audioBlob = new Blob([file], { type: file.type });
    await handleRecordingUpload(audioBlob);
  };

  // ============================================================================
  // Speech-to-Text: Nutzt dieselbe Animation wie Recording,
  // aber fügt Text in die Input-Box ein statt ihn zu senden
  // ============================================================================
  const [isSpeechToTextMode, setIsSpeechToTextMode] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [speechTranscriptionFailed, setSpeechTranscriptionFailed] = useState(false);
  const speechStreamRef = useRef(null);
  const pendingSpeechBlobRef = useRef(null);
  const [audioUploadFailed, setAudioUploadFailed] = useState(false);
  const pendingAudioBlobRef = useRef(null);

  const startSpeechToText = () => {
    setSpeechTranscriptionFailed(false);
    setIsSpeechToTextMode(true);
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        speechStreamRef.current = stream;
        mediaRecorderRef.current = new RecordRTC(stream, {
          type: 'audio',
          mimeType: 'audio/wav',
          recorderType: RecordRTC.StereoAudioRecorder,
          desiredSampRate: 16000,
          numberOfAudioChannels: 1
        });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(stream);
        analyserRef.current = audioContext.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.85;
        source.connect(analyserRef.current);
        dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
        mediaRecorderRef.current.startRecording();
        setRecording(true);
        setPaused(false);
        setShowPlaceholder(false);
        setTimeout(() => { animateWave(); }, 100);
      })
      .catch((error) => {
        console.error('Error accessing media devices.', error);
        setIsSpeechToTextMode(false);
      });
  };

  const transcribeBlob = async (blob, currentApp) => {
    const formData = new FormData();
    formData.append('file', blob, 'speech.wav');
    const response = await api.post(`/chat/speech-to-text/${currentApp}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000
    });
    return response;
  };

  const stopSpeechToText = async () => {
    if (!mediaRecorderRef.current) return;

    if (paused) {
      mediaRecorderRef.current.resumeRecording();
    }

    if (speechStreamRef.current) {
      speechStreamRef.current.getTracks().forEach(track => track.stop());
      speechStreamRef.current = null;
    }

    const currentApp = app;

    mediaRecorderRef.current.stopRecording(async () => {
      const audioBlob = mediaRecorderRef.current.getBlob();
      setRecording(false);
      setPaused(false);
      setIsSpeechToTextMode(false);
      if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);

      setIsTranscribing(true);
      try {
        const response = await transcribeBlob(audioBlob, currentApp);

        if (response.data.success && response.data.text) {
          pendingSpeechBlobRef.current = null;
          setMessage(prev => prev.trim() ? `${prev} ${response.data.text}` : response.data.text);
        } else {
          // Server hat geantwortet aber keinen Text erkannt — Aufnahme aufbewahren
          pendingSpeechBlobRef.current = audioBlob;
          setSpeechTranscriptionFailed(true);
        }
      } catch (error) {
        console.error('Fehler bei Speech-to-Text:', error);
        // Aufnahme aufbewahren damit der User es nochmal versuchen kann
        pendingSpeechBlobRef.current = audioBlob;
        setSpeechTranscriptionFailed(true);
      } finally {
        setIsTranscribing(false);
        setShowPlaceholder(true);
      }
    });
  };

  const retrySpeechToText = async () => {
    const blob = pendingSpeechBlobRef.current;
    if (!blob) return;

    setSpeechTranscriptionFailed(false);
    setIsTranscribing(true);
    try {
      const response = await transcribeBlob(blob, app);

      if (response.data.success && response.data.text) {
        pendingSpeechBlobRef.current = null;
        setMessage(prev => prev.trim() ? `${prev} ${response.data.text}` : response.data.text);
      } else {
        setSpeechTranscriptionFailed(true);
      }
    } catch (error) {
      console.error('Fehler bei Speech-to-Text Retry:', error);
      setSpeechTranscriptionFailed(true);
    } finally {
      setIsTranscribing(false);
      setShowPlaceholder(true);
    }
  };

  const dismissSpeechError = () => {
    setSpeechTranscriptionFailed(false);
    pendingSpeechBlobRef.current = null;
  };

  const retryAudioUpload = async () => {
    const blob = pendingAudioBlobRef.current;
    if (!blob) return;
    setAudioUploadFailed(false);
    pendingAudioBlobRef.current = null;
    await handleRecordingUpload(blob);
  };

  const dismissAudioError = () => {
    setAudioUploadFailed(false);
    pendingAudioBlobRef.current = null;
  };

  const handleRecordingUpload = async (audioBlob) => {
    if (!audioBlob || isProcessing) return;
    
    setIsProcessing(true);
    
    const formData = new FormData();
    const audioFilename = `recording_${Date.now()}.wav`;
    formData.append('file', audioBlob, audioFilename);
    const audioTyping = { 
      user: 'User', 
      text: '', 
      timestamp: new Date().toISOString(), 
      id: 'audio-typing-' + Date.now(), 
      type: 'typing' 
    };
    setMessages(prev => [...prev, audioTyping]);
    
    try {
      // Audio-Upload und Bot-Antwort abrufen
      const botResponse = await api.post(`/chat/${app}/${session._id}/upload/`, formData);
      console.log('🎤 Audio upload response:', botResponse.data);

      if (botResponse.status === 200) {
        const outputData = botResponse.data.output;
        const blobPath = botResponse.data.blobPath; // Blob Storage Pfad
        const isJsonResponse = botResponse.data.is_json_response || isValidJSON(outputData);
        const totalTokens = botResponse.data.total_tokens;
        const citationsData = botResponse.data.citations;

        console.log('📤 Audio Upload Response:', { outputData: outputData?.substring(0, 200), blobPath, isJsonResponse });

        // Generiere SAS URL für Audio-Zugriff
        let audioUrl = null;
        if (blobPath) {
          try {
            const sasResponse = await api.post('/chat/get-sas-url', { blobPath });
            audioUrl = sasResponse.data.url;
            console.log('✅ SAS URL generiert:', audioUrl);
          } catch (error) {
            console.error('❌ Fehler beim Generieren der SAS URL:', error);
          }
        }

        // JSON-Daten extrahieren (gleiche Logik wie bei normalen Nachrichten)
        let jsonData = null;
        if (isJsonResponse) {
          try {
            jsonData = JSON.parse(outputData);
          } catch {
            jsonData = extractJsonFromMarkdownFence(outputData);
          }
        }

        // Text-Formatierung (gleiche Logik wie bei normalen Nachrichten)
        const parsedFromFence = extractJsonFromMarkdownFence(outputData);
        const displayOutput = (parsedFromFence && typeof parsedFromFence === 'object' && typeof parsedFromFence.answer === 'string')
          ? parsedFromFence.answer
          : outputData;

        // WICHTIG: JSON-Responses NICHT mit formatGenericMessage formatieren!
        const botMessageText = isJsonResponse ? outputData : formatGenericMessage(displayOutput);

        const now = Date.now();
        const botMessage = {
          user: 'Bot',
          text: botMessageText,
          timestamp: new Date().toISOString(),
          id: now + 1,
          type: 'msg',
          totalTokens: totalTokens,
          citations: citationsData,
          isJsonResponse: isJsonResponse,
          jsonData: jsonData,
          isCitationResponse: citationsData && Array.isArray(citationsData) && citationsData.length > 0
        };

        // Audio-Nachricht erstellen
        const audioMessageTimestamp = new Date().toISOString();
        const audioMessage = {
          user: 'User',
          text: 'Sprachnachricht',
          blobPath: blobPath,
          audioUrl: audioUrl,
          timestamp: audioMessageTimestamp,
          id: now,
          type: 'audio'
        };

        setMessages(prev => {
          const withoutTyping = prev.filter(m => m.type !== 'typing');
          return [...withoutTyping, audioMessage, botMessage];
        });

        // Berechne display-Titel: Bei Report-JSON verwende den Berichttitel, sonst den ersten Text
        let audioDisplayTitle;
        if (isJsonResponse && jsonData && jsonData['Report Details'] && Array.isArray(jsonData['Report Details']) && jsonData['Report Details'][0]) {
          const reportDetails = jsonData['Report Details'][0];
          const reportTitle = reportDetails['Titel'] || reportDetails['Berichttitel'] || reportDetails['Report Title'] || reportDetails['Title'] || reportDetails['Detaillierter Titel'] || reportDetails['Kurztitel'];
          if (reportTitle) {
            audioDisplayTitle = reportTitle.length > 20 ? reportTitle.substring(0, 20).trim() + '...' : reportTitle;
          } else {
            audioDisplayTitle = String(botMessage.text).replace(/(<([^>]+)>)/gi, '').slice(0, 20) + '...';
          }
        } else {
          audioDisplayTitle = String(botMessage.text).replace(/(<([^>]+)>)/gi, '').slice(0, 25) + '...';
        }

        // Speichere Audio-Nachricht in DB (mit blobPath für späteres Laden)
        await api.post(`/database/sessions/${session._id}/message`, {
          message: {
            user: 'User',
            text: 'Sprachnachricht',
            type: 'audio',
            blobPath: blobPath,
            timestamp: audioMessageTimestamp
          },
          display: audioDisplayTitle
        });

        // Speichere Bot-Antwort in DB
        await api.post(`/database/sessions/${session._id}/message`, {
          message: {
            user: 'Bot',
            text: botMessage.text,
            type: 'msg',
            isJsonResponse: botMessage.isJsonResponse,
            timestamp: new Date().toISOString()
          },
          display: audioDisplayTitle,
        });

        // Sessions neu laden, inkl. der aktuellen Session
        if (refreshSessions) {
          await refreshSessions(session._id);
        }
      } else {
        throw new Error('Failed to upload audio file');
      }
    } catch (error) {
      // Ignoriere abgebrochene Requests (normal beim Session-Wechsel)
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('Audio upload was cancelled (session change)');
        return;
      }

      console.error('Error uploading audio file:', error);
      pendingAudioBlobRef.current = audioBlob;
      setAudioUploadFailed(true);
      setMessages(prev => prev.filter(m => m.type !== 'typing'));
    } finally {
      // Garantiere, dass Processing-State IMMER zurückgesetzt wird
      setIsProcessing(false);
    }
  };

  // Handle no session case after all hooks
  if (!session || !session._id) {
    return (
      <div className="chat-window flex flex-col w-full max-w-3xl mx-5 bg-white h-full overflow-hidden">
        <div className="messages-container">
          <p>Keine Session ausgewählt</p>
        </div>
      </div>
    );
  }

  const isButtonMode = buttonPrompts.length > 0 && messages.filter(m => m.user === 'User').length === 0;

  return (
    <div className="chat-window flex flex-col w-full max-w-3xl mx-5 bg-white h-full overflow-hidden">
      <MessageHeader app={app} sessionId={session._id} messageCount={messages.length} />
      <MessageList messages={messages} messagesEndRef={messagesEndRef} sessionId={session._id} app={app} processingDocument={processingDocument} processingMessage={processingMessage} processingSuccess={processingSuccess} checkAgents={checkAgents} onCheckMessage={handleCheckMessage} checkingMessageId={checkingMessageId} lektoratExport={lektoratExport} />

      {isButtonMode ? (
        <>
          <div className="button-prompt-panel">
            {buttonPrompts.map((bp, i) => (
              <div key={i} className={`button-prompt-card${(isProcessing || (processingDocument && !processingSuccess)) ? ' button-prompt-card--disabled' : ''}`}>
                <div className="button-prompt-card__body" onClick={() => handleButtonPromptClick(bp)}>
                  <div className="button-prompt-card__label">{bp.label}</div>
                  <div className="button-prompt-card__preview">{bp.prompt}</div>
                  {bp.required_documents > 0 && (
                    <div className="button-prompt-card__doc-hint">
                      📎 {bp.required_documents} Dokument{bp.required_documents > 1 ? 'e' : ''} erforderlich
                    </div>
                  )}
                </div>
                <button
                  className="button-prompt-card__expand"
                  onClick={(e) => { e.stopPropagation(); setFullscreenPrompt(bp); }}
                  title="Vollbild anzeigen"
                >
                  ⛶
                </button>
              </div>
            ))}
          </div>
          {fullscreenPrompt && (
            <div className="fullscreen-overlay" onClick={() => setFullscreenPrompt(null)}>
              <div className="fullscreen-modal" onClick={(e) => e.stopPropagation()}>
                <button className="fullscreen-close-button" onClick={() => setFullscreenPrompt(null)}>✕</button>
                <div className="fullscreen-content">
                  <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', color: '#202124' }}>{fullscreenPrompt.label}</h2>
                  <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7', color: '#444' }}>{fullscreenPrompt.prompt}</p>
                </div>
                <div className="button-prompt-fullscreen-footer">
                  <button
                    className="button-prompt-send-btn"
                    onClick={() => { setFullscreenPrompt(null); handleButtonPromptClick(fullscreenPrompt); }}
                    disabled={isProcessing || (processingDocument && !processingSuccess)}
                  >
                    {fullscreenPrompt.required_documents > 0 ? `📎 Dokumente hochladen & senden` : 'Prompt senden'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {pendingButtonPrompt && (
            <div className="fullscreen-overlay" onClick={() => { setPendingButtonPrompt(null); setPendingUploadFiles([]); }}>
              <div className="fullscreen-modal upload-prompt-modal" onClick={(e) => e.stopPropagation()}>
                <button className="fullscreen-close-button" onClick={() => { setPendingButtonPrompt(null); setPendingUploadFiles([]); }}>✕</button>
                <div className="fullscreen-content">
                  <h2 style={{ marginTop: 0, marginBottom: '6px', fontSize: '18px', color: '#202124' }}>{pendingButtonPrompt.label}</h2>
                  <p style={{ color: '#5f6368', fontSize: '13px', marginBottom: '24px', marginTop: 0 }}>
                    Bitte lade {pendingButtonPrompt.required_documents === 1 ? 'ein Dokument' : `${pendingButtonPrompt.required_documents} Dokumente`} hoch, bevor der Prompt gesendet wird.
                  </p>

                  <div
                    className="upload-drop-zone"
                    onClick={() => pendingFileInputRef.current?.click()}
                  >
                    <span className="upload-drop-zone__icon">📎</span>
                    <span className="upload-drop-zone__text">
                      {pendingUploadFiles.length === 0
                        ? `Klicken oder Dateien hierher ziehen (max. ${pendingButtonPrompt.required_documents})`
                        : `${pendingUploadFiles.length} Datei(en) ausgewählt`}
                    </span>
                    <input
                      ref={pendingFileInputRef}
                      type="file"
                      multiple={pendingButtonPrompt.required_documents > 1}
                      style={{ display: 'none' }}
                      onChange={handlePendingFileSelect}
                    />
                  </div>

                  {pendingUploadFiles.length > 0 && (
                    <ul className="upload-file-list">
                      {pendingUploadFiles.map((f, i) => (
                        <li key={i} className="upload-file-list__item">
                          <span className="upload-file-list__name">📄 {f.name}</span>
                          <button
                            className="upload-file-list__remove"
                            onClick={() => setPendingUploadFiles(prev => prev.filter((_, idx) => idx !== i))}
                          >✕</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="button-prompt-fullscreen-footer">
                  <button
                    className="button-prompt-send-btn button-prompt-send-btn--secondary"
                    onClick={() => { setPendingButtonPrompt(null); setPendingUploadFiles([]); }}
                  >
                    Abbrechen
                  </button>
                  <button
                    className="button-prompt-send-btn"
                    onClick={handleUploadAndSend}
                    disabled={pendingUploadFiles.length < pendingButtonPrompt.required_documents}
                  >
                    Hochladen & Senden
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <InputContainer message={message} setMessage={setMessage} handleSendMessage={handleSendMessage} recording={recording} startRecording={startRecording} stopRecording={stopRecording} pauseRecording={pauseRecording} resumeRecording={resumeRecording} showPlaceholder={showPlaceholder} divCount={divCount} animationRef={animationRef} paused={paused} handleNewSessionApp={handleNewSessionApp} app={app} setApp={setApp} session={session} isJsonResponse={isJsonResponse} lastJsonResponse={lastJsonResponse} handleFileUpload={handleFileUpload} toggleSidebar={toggleSidebar} isVisible={isVisible} isProcessing={isProcessing} processingDocument={processingDocument} processingSuccess={processingSuccess} setProcessingDocument={setProcessingDocument} setProcessingMessage={setProcessingMessage} refreshCurrentSession={refreshCurrentSession} startSpeechToText={startSpeechToText} stopSpeechToText={stopSpeechToText} isSpeechToTextMode={isSpeechToTextMode} isTranscribing={isTranscribing} speechTranscriptionFailed={speechTranscriptionFailed} retrySpeechToText={retrySpeechToText} onDismissSpeechError={dismissSpeechError} audioUploadFailed={audioUploadFailed} retryAudioUpload={retryAudioUpload} onDismissAudioError={dismissAudioError} />
      )}
    </div>
  );
};

export default ChatWindow;
